/**
 * #1310 PR-J — `useCatalog` hook for the graph-backed catalog endpoint.
 *
 * Fetches `GET /projects/:id/catalog` (the projection that
 * `apps/api/src/services/analysis/graph/catalogProjection.ts` produces
 * from the AnalysisGraph). Returns a small 6-field-per-row shape
 * instead of the full `E2EFlowPath[]`:
 *
 *   { id, triggerLabel, handlerLabel, classification,
 *     apiCallSummary: { count, methods, sampleEndpoint },
 *     dbSummary: { count, sampleOperation } }
 *
 * Why a thin hook
 * The catalog page currently fetches `/e2e-flows` which returns the
 * full pipeline shape (~70% larger payload — see PR-K for the lazy
 * load). The new endpoint is server-paginated-friendly and avoids
 * shipping `flow.steps[].data` to the browser.
 *
 * PR-J only wires the hook into the read-only count badges.
 * Component prop migration to a discriminated union lands in PR-J2.
 *
 * #1310 PR-M — performance + rollback.
 * - Adds a perf-log: console-warns the fetch duration so we can
 *   observe the latency vs the bulk `/e2e-flows` request that the
 *   page issues in parallel during the bake-in window.
 * - Soft-degrade: when the request fails (404 / 5xx / network), the
 *   hook returns `null` so consumers fall back to deriving counts
 *   from the bulk endpoint. This is the rollback path — the page
 *   keeps working even if the new endpoint is down or the project
 *   hasn't been re-analyzed since the graph substrate landed.
 */
import { useEffect, useState } from 'react';
import { apiFetch } from '../services/api';

export interface CatalogFlow {
  id: string;
  triggerLabel: string;
  handlerLabel: string | null;
  classification: 'clean' | 'needs-review' | 'no-api' | 'dangling';
  /** #1339 item 5 — when dangling, the unmatched api-call endpoint. */
  danglingEndpoint?: string;
  /**
   * #1382 item 6 — one-line reason for non-clean classification.
   * Renders inline on the catalog row as actionable signal instead of
   * the bare `dangling` / `needs-review` chip. Null for clean / no-api.
   */
  chainFailureReason?: string | null;
  apiCallSummary: {
    count: number;
    methods: string[];
    sampleEndpoint?: string;
  };
  dbSummary: {
    count: number;
    sampleOperation?: string;
  };
}

export interface UseCatalogState {
  catalog: CatalogFlow[] | null;
  loading: boolean;
  error: string | null;
}

export function useCatalog(projectId: string | undefined): UseCatalogState {
  const [state, setState] = useState<UseCatalogState>({
    catalog: null,
    loading: false,
    error: null,
  });
  // #1310 PR-P — re-fetch trigger. Bumped by the catalog-invalidate
  // window event (dispatched after a stitch proposal is
  // confirmed/rejected) so the row's classification reflows.
  const [refetchKey, setRefetchKey] = useState(0);
  useEffect(() => {
    function onInvalidate() {
      setRefetchKey(k => k + 1);
    }
    window.addEventListener('veodiagram:catalog-invalidate', onInvalidate);
    return () => {
      window.removeEventListener('veodiagram:catalog-invalidate', onInvalidate);
    };
  }, []);

  useEffect(() => {
    if (!projectId) {
      setState({ catalog: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState(s => ({ ...s, loading: true, error: null }));
    const t0 = performance.now();
    apiFetch(`/projects/${projectId}/catalog`, { credentials: 'include' })
      .then(async r => {
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        const body = await r.json();
        // Endpoint shape: either `CatalogFlow[]` directly or `{ catalog: CatalogFlow[] }`.
        const list = Array.isArray(body) ? body : body?.catalog;
        if (!Array.isArray(list)) {
          throw new Error('catalog endpoint returned unexpected shape');
        }
        return list as CatalogFlow[];
      })
      .then(catalog => {
        if (cancelled) return;
        const ms = Math.round(performance.now() - t0);
        // PR-M perf telemetry — observe the latency for the bake-in
        // window. Drop to console.debug once measurements are
        // collected (planned post-PR-Q).
        console.info(`[#1310 PR-M] useCatalog fetched ${catalog.length} rows in ${ms}ms`);
        setState({ catalog, loading: false, error: null });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        // PR-M soft-degrade — consumers fall back to the bulk
        // `/e2e-flows`-derived counting path when catalog is null.
        console.warn('[#1310 PR-M] useCatalog failed, falling back:', err.message);
        setState({ catalog: null, loading: false, error: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, refetchKey]);

  return state;
}
